/**
 * Trial Service
 * Handles trial period business logic and calculations
 * Integrates safely with existing organization system
 */

import { db } from '../firebaseConfig';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import {
  calculateTrialStatus,
  calculateDaysRemaining,
  calculateTrialProgress,
  canAccessApp,
  isTrialExpired,
  isOnTrial,
  hasActiveSubscription,
  shouldSendNotification,
  validateTrialData
} from '../utils/trialUtils';

class TrialService {
  constructor() {
    this.serviceName = 'TrialService';
  }

  /**
   * Get trial status for an organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} - Trial status information
   */
  async getTrialStatus(organizationId) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const orgRef = doc(db, 'organizations', organizationId);
      const orgDoc = await getDoc(orgRef);

      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }

      const orgData = orgDoc.data();
      
      // Initialize trial data if not present (for existing organizations)
      const trialData = this.initializeTrialData(orgData);
      
      // Helper to normalize Firestore Timestamp -> ISO string (or pass-through)
      const normalizeDate = (value) => {
        if (!value) return null;
        // Firestore Timestamp has toDate()
        if (typeof value.toDate === 'function') {
          return value.toDate().toISOString();
        }
        // Already a Date instance
        if (value instanceof Date) {
          return value.toISOString();
        }
        // Milliseconds number
        if (typeof value === 'number') {
          return new Date(value).toISOString();
        }
        // Assume string (ISO) or other parseable format
        return value;
      };

      // Normalize all date-like fields that could be Firestore Timestamps
      const normalizedTrialStartDate = normalizeDate(trialData.trialStartDate);
      const normalizedTrialEndDate = normalizeDate(trialData.trialEndDate);
      const normalizedSubscriptionStartDate = normalizeDate(trialData.subscriptionStartDate);
      const normalizedLastPaymentDate = normalizeDate(trialData.lastPaymentDate);
      const normalizedNextBillingDate = normalizeDate(trialData.nextBillingDate);
      const normalizedSubscriptionEndsAt = normalizeDate(trialData.subscriptionEndsAt);
      const normalizedCancellationRequestedAt = normalizeDate(trialData.cancellationRequestedAt);

      // Calculate current trial status with normalized values
      const trialStatus = calculateTrialStatus(
        normalizedTrialStartDate,
        normalizedTrialEndDate
      );



      const daysRemaining = calculateDaysRemaining(normalizedTrialEndDate);
      const trialProgress = calculateTrialProgress(
        normalizedTrialStartDate,
        normalizedTrialEndDate
      );

      const isTrialExpiredResult = isTrialExpired(trialData.subscriptionStatus, trialStatus, normalizedSubscriptionEndsAt);

      return {
        organizationId,
        subscriptionStatus: trialData.subscriptionStatus,
        trialStatus,
        daysRemaining,
        trialProgress,
        trialStartDate: normalizedTrialStartDate,
        trialEndDate: normalizedTrialEndDate,
        subscriptionPlan: trialData.subscriptionPlan,
        subscriptionStartDate: normalizedSubscriptionStartDate,
        paymentGatewayId: trialData.paymentGatewayId,
        lastPaymentDate: normalizedLastPaymentDate,
        nextBillingDate: normalizedNextBillingDate,
        willNotRenew: trialData.willNotRenew,
        autoRenew: trialData.autoRenew,
        subscriptionEndsAt: normalizedSubscriptionEndsAt,
        cancellationRequestedAt: normalizedCancellationRequestedAt,
        isTrialExpired: isTrialExpiredResult,
        isOnTrial: isOnTrial(trialData.subscriptionStatus),
        hasActiveSubscription: hasActiveSubscription(trialData.subscriptionStatus),
        trialNotificationsSent: trialData.trialNotificationsSent || {}
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Initialize trial data for existing organizations
   * @param {Object} orgData - Organization data
   * @returns {Object} - Initialized trial data
   */
  initializeTrialData(orgData) {
    // If trial data already exists, return it
    if (orgData.subscriptionStatus && orgData.trialStartDate && orgData.trialEndDate) {
      return orgData;
    }

    // For organizations without trial data, initialize with defaults
    const createdAt = orgData.createdAt || new Date().toISOString();
    const trialStartDate = orgData.trialStartDate || createdAt;
    const trialEndDate = orgData.trialEndDate || this.calculateTrialEndDate(trialStartDate);

    return {
      ...orgData,
      subscriptionStatus: orgData.subscriptionStatus || 'trial',
      trialStartDate,
      trialEndDate,
      subscriptionPlan: orgData.subscriptionPlan || null,
      subscriptionStartDate: orgData.subscriptionStartDate || null,
      paymentGatewayId: orgData.paymentGatewayId || null,
      lastPaymentDate: orgData.lastPaymentDate || null,
      nextBillingDate: orgData.nextBillingDate || null,
      willNotRenew: orgData.willNotRenew || false,
      autoRenew: orgData.autoRenew !== false,
      subscriptionEndsAt: orgData.subscriptionEndsAt || null,
      cancellationRequestedAt: orgData.cancellationRequestedAt || null,
      trialNotificationsSent: orgData.trialNotificationsSent || {
        day7: false,
        day3: false,
        day1: false,
        expired: false
      }
    };
  }

  /**
   * Calculate trial end date (30 days from start)
   * @param {string|Date} startDate - Trial start date
   * @returns {string} - ISO string of trial end date
   */
  calculateTrialEndDate(startDate) {
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + 30);
    return end.toISOString();
  }

  /**
   * Check if user can access the application
   * @param {boolean} isApproved - User/organization approval status
   * @param {string} organizationId - Organization ID
   * @returns {Promise<boolean>} - Whether user can access the app
   */
  async canUserAccessApp(isApproved, organizationId) {
    try {
      if (!isApproved) return false;

      const trialStatus = await this.getTrialStatus(organizationId);
      // FIX: Issue #3 - Pass subscriptionEndsAt to canAccessApp for expiration check
      return canAccessApp(
        isApproved,
        trialStatus.subscriptionStatus,
        trialStatus.trialStatus,
        false, // bypassLimits (not available in this context)
        trialStatus.subscriptionEndsAt || null
      );
    } catch (error) {
      return false;
    }
  }

  /**
   * Update trial data for an organization
   * @param {string} organizationId - Organization ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<void>}
   */
  async updateTrialData(organizationId, updates) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const orgRef = doc(db, 'organizations', organizationId);
      const orgDoc = await getDoc(orgRef);

      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }

      const currentData = this.initializeTrialData(orgDoc.data());
      const mergedData = { ...currentData };

      const normalizeDateValue = (value) => {
        if (!value) return null;

        if (typeof value.toDate === 'function') {
          const date = value.toDate();
          return isNaN(date.getTime()) ? null : date.toISOString();
        }

        if (value instanceof Date) {
          return isNaN(value.getTime()) ? null : value.toISOString();
        }

        if (typeof value === 'number') {
          const date = new Date(value);
          return isNaN(date.getTime()) ? null : date.toISOString();
        }

        if (typeof value === 'string') {
          const date = new Date(value);
          return isNaN(date.getTime()) ? null : date.toISOString();
        }

        return null;
      };

      const applyUpdate = (target, path, value) => {
        if (!path) {
          return;
        }

        if (!path.includes('.')) {
          target[path] = value;
          return;
        }

        const segments = path.split('.');
        const lastKey = segments.pop();
        let cursor = target;

        segments.forEach((segment) => {
          const existingValue = cursor[segment];
          cursor[segment] = { ...(existingValue || {}) };
          cursor = cursor[segment];
        });

        cursor[lastKey] = value;
      };

      Object.entries(updates || {}).forEach(([key, value]) => {
        applyUpdate(mergedData, key, value);
      });

      // Ensure required fields exist before validation
      const normalizedStartDate =
        normalizeDateValue(mergedData.trialStartDate) ||
        normalizeDateValue(currentData.trialStartDate) ||
        normalizeDateValue(currentData.createdAt);

      let normalizedEndDate =
        normalizeDateValue(mergedData.trialEndDate) ||
        normalizeDateValue(currentData.trialEndDate);

      if (!normalizedEndDate && normalizedStartDate) {
        const start = new Date(normalizedStartDate);
        if (!isNaN(start.getTime())) {
          const end = new Date(start);
          end.setDate(end.getDate() + 30);
          normalizedEndDate = end.toISOString();
        }
      }

      mergedData.trialStartDate = normalizedStartDate;
      mergedData.trialEndDate = normalizedEndDate;

      if (!mergedData.subscriptionStatus) {
        mergedData.subscriptionStatus = currentData.subscriptionStatus || 'trial';
      }

      const validation = validateTrialData(mergedData);
      if (!validation.isValid) {
        throw new Error(`Invalid trial data: ${validation.errors.join(', ')}`);
      }

      await updateDoc(orgRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Convert trial to active subscription
   * @param {string} organizationId - Organization ID
   * @param {Object} subscriptionData - Subscription details
   * @returns {Promise<void>}
   */
  async convertTrialToSubscription(organizationId, subscriptionData) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const updates = {
        subscriptionStatus: 'active',
        subscriptionPlan: subscriptionData.plan || 'basic',
        subscriptionStartDate: new Date().toISOString(),
        paymentGatewayId: subscriptionData.paymentGatewayId || null,
        lastPaymentDate: new Date().toISOString(),
        nextBillingDate: subscriptionData.nextBillingDate || null,
        // Clear any cancellation-related fields when converting trial to subscription
        // This ensures data consistency and prevents stale cancellation data from persisting
        // (Edge case: if trial was somehow marked as cancelled, this clears it)
        willNotRenew: false, // Reset to false - new subscription will renew
        autoRenew: true, // Enable auto-renewal for new subscription
        subscriptionEndsAt: null, // Clear any old expiration date
        cancellationRequestedAt: null // Clear any old cancellation timestamp
      };

      await this.updateTrialData(organizationId, updates);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if trial notifications should be sent
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Array>} - Array of notifications to send
   */
  async checkTrialNotifications(organizationId) {
    try {
      const trialStatus = await this.getTrialStatus(organizationId);
      const notifications = [];

      if (!trialStatus.isOnTrial) {
        return notifications;
      }

      const { trialEndDate, trialNotificationsSent } = trialStatus;

      // Check for 7-day notification
      if (shouldSendNotification(trialEndDate, 7, trialNotificationsSent.day7)) {
        notifications.push({
          type: 'day7',
          message: 'Your trial expires in 7 days',
          priority: 'medium'
        });
      }

      // Check for 3-day notification
      if (shouldSendNotification(trialEndDate, 3, trialNotificationsSent.day3)) {
        notifications.push({
          type: 'day3',
          message: 'Your trial expires in 3 days',
          priority: 'high'
        });
      }

      // Check for 1-day notification
      if (shouldSendNotification(trialEndDate, 1, trialNotificationsSent.day1)) {
        notifications.push({
          type: 'day1',
          message: 'Your trial expires tomorrow',
          priority: 'urgent'
        });
      }

      // Check for expired notification
      if (trialStatus.isTrialExpired && !trialNotificationsSent.expired) {
        notifications.push({
          type: 'expired',
          message: 'Your trial has expired',
          priority: 'urgent'
        });
      }

      return notifications;
    } catch (error) {
      return [];
    }
  }

  /**
   * Mark notification as sent
   * @param {string} organizationId - Organization ID
   * @param {string} notificationType - Type of notification
   * @returns {Promise<void>}
   */
  async markNotificationSent(organizationId, notificationType) {
    try {
      const updateKey = `trialNotificationsSent.${notificationType}`;
      await this.updateTrialData(organizationId, {
        [updateKey]: true
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get trial analytics data
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} - Trial analytics
   */
  async getTrialAnalytics(organizationId) {
    try {
      const trialStatus = await this.getTrialStatus(organizationId);
      
      return {
        organizationId,
        trialDuration: 30, // days
        daysRemaining: trialStatus.daysRemaining,
        trialProgress: trialStatus.trialProgress,
        isActive: trialStatus.trialStatus === 'active',
        isExpired: trialStatus.isTrialExpired,
        hasConverted: trialStatus.hasActiveSubscription,
        notificationsSent: Object.values(trialStatus.trialNotificationsSent).filter(Boolean).length
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Extend trial period (for admin use)
   * @param {string} organizationId - Organization ID
   * @param {number} additionalDays - Additional days to add
   * @returns {Promise<void>}
   */
  async extendTrial(organizationId, additionalDays = 7) {
    try {
      if (!organizationId || additionalDays <= 0) {
        throw new Error('Invalid parameters for trial extension');
      }

      const trialStatus = await this.getTrialStatus(organizationId);
      const currentEndDate = new Date(trialStatus.trialEndDate);
      const newEndDate = new Date(currentEndDate);
      newEndDate.setDate(newEndDate.getDate() + additionalDays);

      await this.updateTrialData(organizationId, {
        trialEndDate: newEndDate.toISOString()
      });

    } catch (error) {
      throw error;
    }
  }
}

// Export singleton instance
const trialService = new TrialService();
export default trialService;
